import { Link, NavLink } from 'react-router-dom';
import { ROOMS } from '@/constants/rooms';
import { useAuthStore } from '@/stores/authStore';
import { useTheme } from '@/hooks/useTheme';
import './Sidebar.css';

/**
 * Sidebar — main navigation for JohnicornOS.
 * Shows 9 rooms with icons and accent colors.
 * Collapsible on mobile via prop.
 */
interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

export function Sidebar({ collapsed = false, onToggle }: SidebarProps) {
  const profile = useAuthStore((s) => s.profile);
  const signOut = useAuthStore((s) => s.signOut);
  const { activeTheme, themes, switchTheme } = useTheme();

  return (
    <aside className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''}`}>
      {/* Header */}
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <span className="sidebar-brand-emoji">🦄</span>
          {!collapsed && <span className="sidebar-brand-text">JohnicornOS</span>}
        </div>
        <button className="sidebar-toggle" onClick={onToggle} aria-label="Toggle sidebar">
          {collapsed ? '→' : '←'}
        </button>
      </div>

      {/* Navigation rooms */}
      <nav className="sidebar-nav">
        <ul className="sidebar-rooms">
          {ROOMS.map((room) => (
            <li key={room.id}>
              <NavLink
                to={room.path}
                className={({ isActive }) =>
                  `sidebar-room ${isActive ? 'sidebar-room--active' : ''} ${
                    !room.active ? 'sidebar-room--placeholder' : ''
                  }`
                }
                style={{ '--room-accent': room.accentColor } as React.CSSProperties}
                end={room.path === '/'}
              >
                <span className="sidebar-room-icon">{room.icon}</span>
                {!collapsed && (
                  <>
                    <span className="sidebar-room-label">{room.label}</span>
                    {!room.active && (
                      <span className="sidebar-room-badge">Soon</span>
                    )}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer: Settings + User */}
      <div className="sidebar-footer">
        {!collapsed && (
          <div className="sidebar-theme-picker">
            <div className="sidebar-theme-picker__label">Theme</div>
            <details className="sidebar-theme-picker__popover">
              <summary className="sidebar-theme-picker__trigger">
                <span className="sidebar-theme-dot" style={{ background: activeTheme.swatches[0] }} />
                <span>{activeTheme.name}</span>
              </summary>
              <div className="sidebar-theme-picker__menu">
                {themes.map((theme) => (
                  <button
                    key={theme.id}
                    type="button"
                    className={`sidebar-theme-item ${activeTheme.id === theme.id ? 'sidebar-theme-item--active' : ''}`}
                    onClick={() => void switchTheme(theme.id)}
                  >
                    <span className="sidebar-theme-swatches">
                      <i style={{ background: theme.swatches[0] }} />
                      <i style={{ background: theme.swatches[1] }} />
                      <i style={{ background: theme.swatches[2] }} />
                    </span>
                    <span className="sidebar-theme-meta">
                      <b>{theme.name}</b>
                      <small>{theme.material}</small>
                    </span>
                  </button>
                ))}
              </div>
            </details>
          </div>
        )}

        <NavLink
          to="/settings"
          className={({ isActive }) =>
            `sidebar-room ${isActive ? 'sidebar-room--active' : ''}`
          }
          style={{ '--room-accent': 'hsl(220, 15%, 55%)' } as React.CSSProperties}
        >
          <span className="sidebar-room-icon">⚙️</span>
          {!collapsed && <span className="sidebar-room-label">Settings</span>}
        </NavLink>

        {profile?.role === 'admin' && (
          <NavLink
            to="/admin"
            className={({ isActive }) =>
              `sidebar-room ${isActive ? 'sidebar-room--active' : ''}`
            }
            style={{ '--room-accent': '#EF4444' } as React.CSSProperties}
          >
            <span className="sidebar-room-icon">🛡️</span>
            {!collapsed && <span className="sidebar-room-label">Admin</span>}
          </NavLink>
        )}

        {!collapsed && (
          <div className="sidebar-legal">
            <Link to="/privacy">Privacy</Link>
            <span aria-hidden="true"> · </span>
            <Link to="/terms">Terms</Link>
          </div>
        )}

        {!collapsed && profile && (
          <div className="sidebar-user">
            <div className="sidebar-user-avatar">
              {profile.display_name?.[0]?.toUpperCase() ?? '?'}
            </div>
            <div className="sidebar-user-info">
              <span className="sidebar-user-name">{profile.display_name ?? 'User'}</span>
              <span className="sidebar-user-level">
                {profile.email}
              </span>
            </div>
            <button className="sidebar-logout" onClick={signOut} title="Sign out">
              🚪
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
